import React, { useRef, useEffect, useContext, useState, useCallback } from 'react';
import { FaBell } from 'react-icons/fa';
import { gsap } from 'gsap';
import { NotificationsContext } from '../context/NotificationsContext';

const NotificationIcon = () => {
  const iconRef = useRef(null);
  const btnRef = useRef(null);
  const { unreadCount, openModal } = useContext(NotificationsContext);
  const [position, setPosition] = useState(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);

  const BUTTON_SIZE = 42;
  const BADGE_SIZE = 17;
  const EDGE_MARGIN = 16;

  const clamp = useCallback((value, min, max) => Math.min(Math.max(value, min), max), []);

  useEffect(() => {
    // Ensure the button starts in the bottom-right corner and stays on-screen after resizes
    const ensurePositionInView = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const defaultPosition = {
        x: viewportWidth - BUTTON_SIZE - EDGE_MARGIN,
        y: viewportHeight - BUTTON_SIZE - EDGE_MARGIN,
      };
      setPosition((prev) => {
        const base = prev ?? defaultPosition;
        return {
          x: clamp(base.x, EDGE_MARGIN, viewportWidth - BUTTON_SIZE - EDGE_MARGIN),
          y: clamp(base.y, EDGE_MARGIN, viewportHeight - BUTTON_SIZE - EDGE_MARGIN),
        };
      });
    };

    ensurePositionInView();
    window.addEventListener('resize', ensurePositionInView);
    return () => window.removeEventListener('resize', ensurePositionInView);
  }, [BUTTON_SIZE, EDGE_MARGIN, clamp]);

  useEffect(() => {
    if (iconRef.current) {
      gsap.fromTo(
        iconRef.current,
        { y: -8 },
        { y: 0, duration: 0.7, ease: 'bounce.out' }
      );
    }
  }, []);

  // Pulse on hover
  useEffect(() => {
    if (!btnRef.current) return;
    const el = btnRef.current;
    el.style.touchAction = 'none';
    el.style.cursor = 'grab';
    el.style.userSelect = 'none';
    const handleMouseEnter = () => {
      gsap.to(el, { scale: 1.12, boxShadow: '0 4px 18px rgba(25,118,210,0.18)', duration: 0.25 });
    };
    const handleMouseLeave = () => {
      gsap.to(el, { scale: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', duration: 0.25 });
    };
    el.addEventListener('mouseenter', handleMouseEnter);
    el.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      el.removeEventListener('mouseenter', handleMouseEnter);
      el.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const handlePointerDown = useCallback((event) => {
    if (!btnRef.current) return;

    event.preventDefault();
    const el = btnRef.current;
    const target = event.currentTarget;

    if (!el) return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const currentPosition = position ?? {
      x: viewportWidth - BUTTON_SIZE - EDGE_MARGIN,
      y: viewportHeight - BUTTON_SIZE - EDGE_MARGIN,
    };

    dragOffsetRef.current = {
      x: event.clientX - currentPosition.x,
      y: event.clientY - currentPosition.y,
    };

    isDraggingRef.current = false;
    el.style.cursor = 'grabbing';

    if (target.setPointerCapture) {
      try {
        target.setPointerCapture(event.pointerId);
      } catch (_) {
        // Pointer capture may fail on some devices
      }
    }

    const handlePointerMove = (moveEvent) => {
      moveEvent.preventDefault();
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;

      const nextX = clamp(
        moveEvent.clientX - dragOffsetRef.current.x,
        EDGE_MARGIN,
        viewportW - BUTTON_SIZE - EDGE_MARGIN
      );
      const nextY = clamp(
        moveEvent.clientY - dragOffsetRef.current.y,
        EDGE_MARGIN,
        viewportH - BUTTON_SIZE - EDGE_MARGIN
      );

      if (!isDraggingRef.current && (Math.abs(nextX - currentPosition.x) > 1 || Math.abs(nextY - currentPosition.y) > 1)) {
        isDraggingRef.current = true;
      }

      setPosition({ x: nextX, y: nextY });
    };

    const endDrag = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      if (target.releasePointerCapture) {
        try {
          target.releasePointerCapture(event.pointerId);
        } catch (_) {
          // no-op: pointer already released
        }
      }
      el.style.cursor = 'grab';
      // Delay clearing the drag flag so click handlers can read it accurately
      requestAnimationFrame(() => {
        isDraggingRef.current = false;
      });
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: false });
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
  }, [BUTTON_SIZE, EDGE_MARGIN, clamp, position]);

  const handleClick = useCallback(() => {
    if (isDraggingRef.current) return;
    openModal();
  }, [openModal]);

  return (
    <>
      <div
        ref={btnRef}
        style={{
          position: 'fixed',
          top: position ? position.y : undefined,
          left: position ? position.x : undefined,
          bottom: position ? undefined : 32,
          right: position ? undefined : 32,
          zIndex: 2000,
          background: 'linear-gradient(135deg, #e3f0ff 0%, #ffffff 100%)',
          borderRadius: '50%',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          width: `${BUTTON_SIZE}px`,
          height: `${BUTTON_SIZE}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2.5px solid #1976d2',
          cursor: 'pointer',
          transition: 'box-shadow 0.2s, transform 0.2s',
          userSelect: 'none',
        }}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
      >
        <FaBell ref={iconRef} size={22} color="#1976d2" />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 7,
              right: 7,
              background: 'linear-gradient(135deg, #ff1744 0%, #ff5252 100%)',
              color: '#fff',
              borderRadius: '50%',
              minWidth: BADGE_SIZE,
              height: BADGE_SIZE,
              fontSize: 12,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 6px rgba(255,23,68,0.18)',
              border: '2px solid #fff',
              padding: '0 4px',
            }}
          >
            {unreadCount}
          </span>
        )}
      </div>
    </>
  );
};

export default NotificationIcon; 